import * as React from 'react';
import { create } from 'zustand';

import type { ToastActionElement } from './toast';

const TOAST_AUTO_DISMISS_MS = 6000;

export interface ToastItem {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  variant?: 'default' | 'success' | 'danger';
  open: boolean;
}

interface ToastStoreState {
  toasts: ToastItem[];
  show(toast: Omit<ToastItem, 'id' | 'open'>): string;
  dismiss(id?: string): void;
  remove(id?: string): void;
}

let toastCount = 0;
function nextId(): string {
  toastCount += 1;
  return `toast_${toastCount}`;
}

/**
 * Toast queue as a Zustand store — matches this app's "Zustand for
 * client/UI state" convention instead of shadcn's usual bespoke
 * Context+reducer implementation. `toast(...)` is callable from anywhere
 * (event handlers, query error callbacks), not just inside components.
 */
export const useToastStore = create<ToastStoreState>((set) => ({
  toasts: [],
  show(toastInput) {
    const id = nextId();
    set((state) => ({
      toasts: [...state.toasts, { ...toastInput, id, open: true }].slice(-3),
    }));
    return id;
  },
  dismiss(id) {
    set((state) => ({
      toasts: state.toasts.map((t) => (id === undefined || t.id === id ? { ...t, open: false } : t)),
    }));
  },
  remove(id) {
    set((state) => ({
      toasts: id === undefined ? [] : state.toasts.filter((t) => t.id !== id),
    }));
  },
}));

export function toast(input: Omit<ToastItem, 'id' | 'open'>): string {
  const id = useToastStore.getState().show(input);
  setTimeout(() => useToastStore.getState().dismiss(id), TOAST_AUTO_DISMISS_MS);
  return id;
}

export function useToast() {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);
  return { toasts, toast, dismiss };
}
